/**
 * Shared lazy Mermaid loader. Mermaid (~heavy) is imported on first use and
 * initialized exactly once, themed to the Dispatch Desk palette, so it never
 * touches the app's initial bundle — only routes that actually render a
 * diagram pay for it.
 *
 * securityLevel 'strict' is load-bearing: charts can come from agent/user
 * markdown (issue bodies, PM comments), and strict mode sanitizes labels and
 * disables click/javascript directives in untrusted chart text.
 */

export type MermaidApi = Awaited<typeof import('mermaid')>['default'];

// Dispatch Desk tokens (light-only) mapped onto Mermaid's base theme so the
// diagrams read as part of the app, not default-purple Mermaid.
const THEME_VARIABLES = {
  background: '#FBFAF8',
  primaryColor: '#FBFAF8', // node fill — surface-1
  primaryBorderColor: '#B9B7B0', // line-strong
  primaryTextColor: '#212326', // ink
  secondaryColor: '#F0EFEB', // surface-2
  tertiaryColor: '#E9E8E4', // surface-0
  lineColor: '#6B6E71', // ink-muted — edges
  textColor: '#212326',
  nodeBorder: '#B9B7B0',
  mainBkg: '#FBFAF8',
  clusterBkg: '#F0EFEB',
  clusterBorder: '#D7D5CF',
  edgeLabelBackground: '#FBFAF8',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
  fontSize: '13px',
};

let mermaidSingleton: Promise<MermaidApi> | null = null;

export function getMermaid(): Promise<MermaidApi> {
  if (!mermaidSingleton) {
    mermaidSingleton = import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: THEME_VARIABLES,
        securityLevel: 'strict',
        flowchart: { curve: 'basis', padding: 14, useMaxWidth: true },
      });
      return mermaid;
    });
  }
  return mermaidSingleton;
}
